/**
 * Doctor Command
 * Diagnose the current project directory for common issues
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { promises as fs } from 'fs';
import { join } from 'path';
import { ConfigService } from '../services/config.service.js';
import { IdeDetectionService } from '../services/ide-detection.service.js';
import { ProjectInspectorService } from '../services/project-inspector.service.js';
import { detectInstalledPrimaryPackage } from '../services/primary-package-policy.service.js';
import type { ProjectInspectionResult } from '../types/project-inspector.types.js';
import { IDE_MIGRATION_CONFIGS } from '../constants/ide-migration-configs.js';
import type { MigrateIDE } from '../types/migrate-ide.types.js';
import { getCliName } from '../utils/cli-name.js';

// Minimum core files that must exist (fallback when manifest.json is not available)
const CORE_FILES_FALLBACK = [
    'context/jv-it-context.md',
    'rules/jai1.md',
];

export interface CheckResult {
    name: string;
    passed: boolean;
    message: string;
    details?: string[];
    suggestion?: string;
}

export interface CoreV3IdeDiagnostics {
    managedTargets: MigrateIDE[];
    completeTargets: MigrateIDE[];
    missing: Partial<Record<MigrateIDE, string[]>>;
}

export function createDoctorCommand(): Command {
    const cmd = new Command('doctor')
        .description('Chuẩn đoán project hiện tại')
        .option('--json', 'Output as JSON')
        .action(async (_options: { json?: boolean }, command: Command) => {
            const options = command.optsWithGlobals<{ json?: boolean }>();
            await runDoctor({ json: options.json === true });
        });

    return cmd;
}

async function runDoctor(options: { json?: boolean }): Promise<void> {
    const name = getCliName();
    const results: CheckResult[] = [];

    if (!options.json) {
        console.log(chalk.cyan.bold('\n🩺 Jai1 Doctor\n'));
        console.log(chalk.dim(`  Đang kiểm tra project tại: ${process.cwd()}\n`));
    }

    // ── Check 1: Auth ──
    results.push(await checkAuth(name));

    const primaryState = await checkPrimaryPackageState(name);
    results.push(primaryState.result);
    const installedPrimary = primaryState.installedPrimary;
    const isCoreV3 = installedPrimary === 'core-v3';

    // ── Check 2: Core package files or adaptive core-v3 state ──
    results.push(isCoreV3
        ? buildCoreV3DoctorCheck(await new ProjectInspectorService().inspect(), name)
        : await checkCoreFiles(name));

    // ── Check 3 & 4: IDE detection + rules/workflows ──
    const [ideCheck, ideRulesCheck, coreV3Ide] = await checkIde(name, isCoreV3);
    results.push(ideCheck);
    results.push(ideRulesCheck);

    // ── Output ──
    if (options.json) {
        console.log(JSON.stringify({ results, ...(coreV3Ide && { coreV3Ide }) }, null, 2));
        return;
    }

    for (const result of results) {
        const icon = result.passed ? chalk.green('✓') : chalk.red('✗');
        const label = result.passed
            ? chalk.green(result.message)
            : chalk.red(result.message);

        console.log(`  ${icon}  ${chalk.bold(result.name)}: ${label}`);

        if (result.details && result.details.length > 0) {
            for (const detail of result.details) {
                console.log(chalk.dim(`     ${detail}`));
            }
        }

        if (!result.passed && result.suggestion) {
            const lines = result.suggestion.split('\n');
            console.log(chalk.yellow(`     💡 ${lines[0]}`));
            for (const line of lines.slice(1)) {
                console.log(chalk.yellow(`        ${line}`));
            }
        }

        console.log();
    }

    // ── Summary ──
    const passed = results.filter((r) => r.passed).length;
    const total = results.length;

    if (passed === total) {
        console.log(chalk.green.bold(`  ✅ Tất cả ${total} kiểm tra đã pass!\n`));
    } else {
        console.log(
            chalk.yellow(`  ⚠️  ${passed}/${total} kiểm tra pass. Xem gợi ý ở trên để sửa.\n`)
        );
    }
}

export async function checkPrimaryPackageState(
    cliName: string,
    projectRoot: string = process.cwd(),
): Promise<{ result: CheckResult; installedPrimary: string | null }> {
    try {
        const installedPrimary = await detectInstalledPrimaryPackage(projectRoot);
        return {
            result: {
                name: 'Primary Package State',
                passed: true,
                message: installedPrimary
                    ? `Primary package nhất quán: ${installedPrimary}`
                    : 'Không phát hiện primary package xung đột',
            },
            installedPrimary,
        };
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return {
            result: {
                name: 'Primary Package State',
                passed: false,
                message,
                suggestion: `Kiểm tra .jai1/project.json, .jai1/manifest.json và .jai1/rules/ trước khi chạy lại "${cliName} doctor".`,
            },
            installedPrimary: null,
        };
    }
}

export function buildCoreV3DoctorCheck(
    inspection: ProjectInspectionResult,
    cliName: string,
): CheckResult {
    const passed = inspection.status === 'ready';
    const changed = inspection.changedSignals.length > 0
        ? [`Tín hiệu thay đổi: ${inspection.changedSignals.join(', ')}`]
        : undefined;

    return {
        name: 'Core-v3 Project',
        passed,
        message: `Trạng thái adaptive project: ${inspection.status}`,
        details: changed,
        ...(!passed && {
            suggestion: `Chạy "${cliName} project inspect" để xem tín hiệu dự án, sau đó dùng /jai1 để review và cập nhật proposal.`,
        }),
    };
}

export function getCoreV3IdeSuggestion(cliName: string): string {
    return `Chạy "${cliName} ide sync" để đồng bộ kernel, entry jai1 và project skills sang IDE.`;
}

const CORE_V3_CAPABILITY_SKILLS = [
    'jai1-bootstrap',
    'jai1-plan',
    'jai1-build',
    'jai1-debug',
    'jai1-verify',
    'jai1-ship',
    'jai1-learn',
    'jai1-maintain',
] as const;

export async function inspectCoreV3IdeArtifacts(
    projectRoot: string,
    ide: MigrateIDE,
): Promise<{ complete: boolean; missing: string[] }> {
    const config = IDE_MIGRATION_CONFIGS[ide];
    const missing: string[] = [];

    if (ide === 'opencode' || ide === 'codex') {
        try {
            const agents = await fs.readFile(join(projectRoot, 'AGENTS.md'), 'utf-8');
            if (!/Jai1 v3 Adaptive Kernel/i.test(agents)) missing.push('kernel:jai1-v3');
        } catch {
            missing.push('kernel:jai1-v3');
        }
    } else {
        const kernelPath = config.rulesPath
            ? join(projectRoot, config.basePath, config.rulesPath, `jai1-v3${config.fileExtension}`)
            : '';
        if (!kernelPath || !(await fileExists(kernelPath))) missing.push('kernel:jai1-v3');
    }

    if (ide === 'codex') {
        const publicSkill = join(projectRoot, '.agents', 'skills', 'jai1', 'SKILL.md');
        if (!(await fileExists(publicSkill))) missing.push('public:jai1');
    } else {
        const publicDirectory = config.workflowsPath ?? config.commandsPath;
        const extension = config.workflowsExtension ?? config.fileExtension;
        const publicPath = publicDirectory
            ? join(projectRoot, config.basePath, publicDirectory, `jai1${extension}`)
            : '';
        if (!publicPath || !(await fileExists(publicPath))) missing.push('public:jai1');
    }

    for (const skill of CORE_V3_CAPABILITY_SKILLS) {
        const skillPath = config.skillsPath
            ? join(projectRoot, config.basePath, config.skillsPath, skill, 'SKILL.md')
            : '';
        if (!skillPath || !(await fileExists(skillPath))) missing.push(`skill:${skill}`);
    }

    return { complete: missing.length === 0, missing };
}

export async function discoverCoreV3ManagedTargets(
    projectRoot: string,
): Promise<MigrateIDE[]> {
    const targets: MigrateIDE[] = [];

    for (const ide of Object.keys(IDE_MIGRATION_CONFIGS) as MigrateIDE[]) {
        const config = IDE_MIGRATION_CONFIGS[ide];
        let publicPath: string;

        if (ide === 'codex') {
            publicPath = join(projectRoot, '.agents', 'skills', 'jai1', 'SKILL.md');
        } else {
            const publicDirectory = config.workflowsPath ?? config.commandsPath;
            const extension = config.workflowsExtension ?? config.fileExtension;
            if (!publicDirectory) continue;
            publicPath = join(projectRoot, config.basePath, publicDirectory, `jai1${extension}`);
        }

        if (await fileExists(publicPath)) targets.push(ide);
    }

    return targets;
}

async function fileExists(filepath: string): Promise<boolean> {
    try {
        await fs.access(filepath);
        return true;
    } catch {
        return false;
    }
}

// ────────────────────────────────────────────────────────────
// Check 1: Auth with server
// ────────────────────────────────────────────────────────────
async function checkAuth(cliName: string): Promise<CheckResult> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        return {
            name: 'Auth',
            passed: false,
            message: 'Chưa cấu hình xác thực',
            suggestion: `Chạy "${cliName} auth" để đăng nhập.`,
        };
    }

    // Try to verify with server
    try {
        const response = await fetch(`${config.apiUrl}/api/versions/framework`, {
            headers: { 'JAI1-Access-Key': config.accessKey },
            signal: AbortSignal.timeout(10000), // 10s timeout
        });

        if (response.ok) {
            return {
                name: 'Auth',
                passed: true,
                message: `Kết nối thành công đến ${config.apiUrl}`,
            };
        }

        if (response.status === 401 || response.status === 403) {
            return {
                name: 'Auth',
                passed: false,
                message: `Access key không hợp lệ (HTTP ${response.status})`,
                suggestion: `Chạy "${cliName} auth" để cấu hình lại.`,
            };
        }

        return {
            name: 'Auth',
            passed: false,
            message: `Server trả về HTTP ${response.status}`,
            suggestion: `Kiểm tra lại API URL hoặc thử lại sau.`,
        };
    } catch (error) {
        const errMsg =
            error instanceof Error ? error.message : String(error);
        return {
            name: 'Auth',
            passed: false,
            message: `Không thể kết nối đến server`,
            details: [errMsg],
            suggestion: `Kiểm tra kết nối mạng hoặc API URL (${config.apiUrl}).`,
        };
    }
}

// ────────────────────────────────────────────────────────────
// Check 2: .jai1 directory + core package files
// Uses manifest.json to dynamically determine which files to check
// Supports both flat (core v1) and subdirectory (core v2) layouts
// ────────────────────────────────────────────────────────────
async function checkCoreFiles(cliName: string): Promise<CheckResult> {
    const jai1Dir = join(process.cwd(), '.jai1');

    // Check .jai1 directory exists
    try {
        await fs.access(jai1Dir);
    } catch {
        return {
            name: 'Core Package',
            passed: false,
            message: 'Thư mục .jai1 chưa tồn tại',
            suggestion: `Chạy "${cliName} apply core" để cài đặt core package.`,
        };
    }

    // Read manifest.json to get list of installed core files
    let coreFiles: string[] = CORE_FILES_FALLBACK;
    try {
        const manifestPath = join(jai1Dir, 'manifest.json');
        const manifestContent = await fs.readFile(manifestPath, 'utf-8');
        const manifest = JSON.parse(manifestContent) as Record<string, { filepath: string }>;
        const manifestFiles = Object.keys(manifest);
        if (manifestFiles.length > 0) {
            coreFiles = manifestFiles;
        }
    } catch {
        // No manifest.json — use fallback list
    }

    // Check core files (supports both flat and subdirectory paths)
    const missing: string[] = [];
    const found: string[] = [];

    for (const file of coreFiles) {
        const filePath = join(jai1Dir, file);
        try {
            await fs.access(filePath);
            found.push(file);
        } catch {
            missing.push(file);
        }
    }

    if (missing.length === 0) {
        return {
            name: 'Core Package',
            passed: true,
            message: `Đầy đủ ${coreFiles.length} core files`,
        };
    }

    if (found.length === 0) {
        return {
            name: 'Core Package',
            passed: false,
            message: `Không có core file nào (0/${coreFiles.length})`,
            suggestion: `Chạy "${cliName} apply core" để cài đặt core package.`,
        };
    }

    return {
        name: 'Core Package',
        passed: false,
        message: `Thiếu ${missing.length}/${coreFiles.length} core files`,
        details: missing.map((f) => `Thiếu: ${f}`),
        suggestion: `Chạy "${cliName} update" để cập nhật, hoặc "${cliName} apply core" để cài lại.`,
    };
}

// ────────────────────────────────────────────────────────────
// Check 3 & 4: IDE detection + rules/workflows
// ────────────────────────────────────────────────────────────
async function checkIde(
    cliName: string,
    isCoreV3 = false,
): Promise<[CheckResult, CheckResult, CoreV3IdeDiagnostics | undefined]> {
    const service = new IdeDetectionService();
    const activeIdes = isCoreV3
        ? (await discoverCoreV3ManagedTargets(process.cwd())).map((id) => ({
            id,
            name: IDE_MIGRATION_CONFIGS[id].name,
            detected: true,
            hasRules: false,
            hasWorkflows: false,
            ruleCount: 0,
            workflowCount: 0,
            confidence: 'high' as const,
        }))
        : await service.detectActiveIdes();

    // Check 3: At least 1 IDE deployed
    if (activeIdes.length === 0) {
        const diagnostics: CoreV3IdeDiagnostics | undefined = isCoreV3
            ? { managedTargets: [], completeTargets: [], missing: {} }
            : undefined;
        return [
            {
                name: 'IDE Deployment',
                passed: false,
                message: 'Chưa triển khai IDE nào',
                suggestion: isCoreV3
                    ? getCoreV3IdeSuggestion(cliName)
                    : `Chạy "${cliName} rules apply" để present có sẳn hoặc dùng AI chạy workflow /generate-rules-present để tạo ra`,
            },
            {
                name: 'IDE Rules & Workflows',
                passed: false,
                message: 'Không có IDE để kiểm tra',
                suggestion: `Triển khai ít nhất 1 IDE trước.`,
            },
            diagnostics,
        ];
    }

    const ideNames = activeIdes.map((ide) => ide.name).join(', ');
    const ideCheck: CheckResult = {
        name: 'IDE Deployment',
        passed: true,
        message: `${activeIdes.length} IDE được phát hiện: ${ideNames}`,
    };

    // Check 4: Rules & Workflows — pass if at least 1 IDE has both
    const details: string[] = [];
    const fullyConfigured: string[] = [];
    const completeTargets: MigrateIDE[] = [];
    const missingByTarget: Partial<Record<MigrateIDE, string[]>> = {};
    let managedCoreV3Ides = 0;

    for (const ide of activeIdes) {
        if (isCoreV3) {
            if (!(ide.id in IDE_MIGRATION_CONFIGS)) {
                details.push(`${ide.name}: không phải target do j ide sync quản lý`);
                continue;
            }
            managedCoreV3Ides++;
            const artifacts = await inspectCoreV3IdeArtifacts(process.cwd(), ide.id as MigrateIDE);
            details.push(`${ide.name}: ${artifacts.complete ? 'core-v3 ✓' : `thiếu ${artifacts.missing.join(', ')}`}`);
            if (artifacts.complete) {
                fullyConfigured.push(ide.name);
                completeTargets.push(ide.id as MigrateIDE);
            } else {
                missingByTarget[ide.id as MigrateIDE] = artifacts.missing;
            }
            continue;
        }

        const rulesIcon = ide.hasRules ? '✓' : '✗';
        const workflowsIcon = ide.hasWorkflows ? '✓' : '✗';

        details.push(
            `${ide.name}: rules ${rulesIcon} (${ide.ruleCount}) | workflows ${workflowsIcon} (${ide.workflowCount})`
        );

        if (ide.hasRules && ide.hasWorkflows) {
            fullyConfigured.push(ide.name);
        }
    }

    const passed = isCoreV3
        ? managedCoreV3Ides > 0 && fullyConfigured.length === managedCoreV3Ides
        : fullyConfigured.length > 0;

    const rulesWorkflowsCheck: CheckResult = {
        name: 'IDE Rules & Workflows',
        passed,
        message: passed
            ? isCoreV3
                ? `${fullyConfigured.length} IDE đầy đủ kernel, jai1 entry và 8 capability skills: ${fullyConfigured.join(', ')}`
                : `${fullyConfigured.length} IDE đầy đủ rules & workflows: ${fullyConfigured.join(', ')}`
            : isCoreV3
                ? 'Chưa có IDE nào nhận đủ core-v3 entry và workflows'
                : 'Chưa có IDE nào đầy đủ cả rules và workflows',
        details,
        ...(!passed && {
            suggestion: isCoreV3 ? getCoreV3IdeSuggestion(cliName) : [
                `Có nhiều cách để tạo rules cho project:`,
                `  - Tự tìm trên internet phù hợp với techstack của project và sửa lại.`,
                `  - Tìm xem có sẵn rule-preset nào trên jai1 store không bằng lệnh: "${cliName} rules apply".`,
                `  - Chạy workflow của jai1 "/generate-rules-preset" để tạo rule-preset cho project, sau đó đồng bộ cho các IDE dùng lệnh "${cliName} ide sync".`,
            ].join('\n'),
        }),
    };

    const diagnostics: CoreV3IdeDiagnostics | undefined = isCoreV3
        ? {
            managedTargets: activeIdes.map((ide) => ide.id as MigrateIDE),
            completeTargets,
            missing: missingByTarget,
        }
        : undefined;

    return [ideCheck, rulesWorkflowsCheck, diagnostics];
}
